/**
 * MicroApiChannel - Event-based message passing between SDK and spawned windows.
 *
 * Uses CustomEvent on window since SDK and spawned windows run in the same
 * JavaScript context (no iframe isolation). Channels are discoverable via
 * a global registry.
 *
 * Channel ID format: `{parentWindowId}:{childWindowId}`
 */
class MicroApiChannel {
  constructor(channelId) {
    this.channelId = channelId;
    this.listeners = new Map();
    this._register();
  }

  /**
   * Send a message on this channel.
   * @param {string} type - Message type (e.g., 'data', 'command', 'lifecycle')
   * @param {*} payload - Message payload
   */
  send(type, payload) {
    const event = new CustomEvent(`micro-api:${this.channelId}`, {
      detail: { type, payload, timestamp: Date.now() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Listen for messages on this channel.
   * @param {string} type - Message type to listen for (or '*' for all)
   * @param {Function} callback - Handler: (payload, type) => void
   * @returns {Function} Unsubscribe function
   */
  on(type, callback) {
    const handler = (event) => {
      const { type: msgType, payload } = event.detail;
      if (type === '*' || msgType === type) {
        callback(payload, msgType);
      }
    };

    window.addEventListener(`micro-api:${this.channelId}`, handler);

    const unsubscribe = () => {
      window.removeEventListener(`micro-api:${this.channelId}`, handler);
    };

    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push({ handler, unsubscribe });

    return unsubscribe;
  }

  /**
   * Clean up all listeners and unregister.
   */
  destroy() {
    for (const [, handlers] of this.listeners) {
      handlers.forEach(({ unsubscribe }) => unsubscribe());
    }
    this.listeners.clear();
    this._unregister();
  }

  _register() {
    window.__microApiChannels = window.__microApiChannels || {};
    window.__microApiChannels[this.channelId] = this;
  }

  _unregister() {
    if (window.__microApiChannels) {
      delete window.__microApiChannels[this.channelId];
    }
  }

  /**
   * Get an existing channel by ID.
   * @param {string} channelId
   * @returns {MicroApiChannel|null}
   */
  static getChannel(channelId) {
    return window.__microApiChannels?.[channelId] || null;
  }
}

export default MicroApiChannel;
